import React, { useState } from "react"
import PropTypes from "prop-types"
import { Button, Flex, Input } from "antd"
import { useMediaQuery } from "react-responsive"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import { numberToCurrency } from "helpers/string"
import { roundToCents } from "utils/bookingCalculations"
import { isPromoCheckRateLimited } from "utils/discountUtils"
import { getExperienceCoverImage } from "../helpers"
import { groupBreakdownLines, getOriginalIfDiscounted } from "../WidgetExperience/pricing"
import { describeBookingDateLines, describeGuests, describeTickets, isTicketedSelection } from "../bookingDescription"
import styles from "./WidgetCheckout.module.css"

// Promo code for the order: Add Promo-code -> code field (Cancel / Apply, error when invalid) ->
// "Promo- code applied" with Change / Remove. One code for the whole order; onValidate checks it
// with the API and applies it, or rejects with the API's error. With several experiences, their taxes
// and fees (each worked out on its own experience) are added up in one line above the Order Total.
const PromoSection = ({ amounts, appliedPromo, isTaxesLineShown, onValidate, onRemove }) => {
  const { t } = useTranslation()
  const [isEditing, setIsEditing] = useState(false)
  const [code, setCode] = useState("")
  const [error, setError] = useState(null)
  const [isValidating, setIsValidating] = useState(false)

  const handleApply = async () => {
    const trimmed = code.trim().toUpperCase()
    if (!trimmed) return
    setIsValidating(true)
    setError(null)
    try {
      await onValidate(trimmed)
      setIsEditing(false)
      setCode("")
    } catch (err) {
      setError(
        isPromoCheckRateLimited(err)
          ? t("booking_widget.checkout.errors.promo_rate_limited")
          : err?.data?.message?.base?.[0] || t("booking_widget.checkout.errors.promo_invalid")
      )
    } finally {
      setIsValidating(false)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
    setCode("")
    setError(null)
  }

  const handleChange = () => {
    setCode(appliedPromo?.code || "")
    onRemove(appliedPromo?.code)
    setIsEditing(true)
  }

  let promoBlock
  if (appliedPromo) {
    promoBlock = (
      <Flex justify="space-between" align="flex-start" gap={12}>
        <Flex vertical>
          <span>{t("booking_widget.checkout.promo_applied")}</span>
          <span>
            {appliedPromo.code} {appliedPromo.label}
          </span>
        </Flex>
        <Flex gap={16}>
          <Button type="link" className={styles.linkButton} onClick={handleChange}>
            {t("booking_widget.checkout.change")}
          </Button>
          <Button type="link" className={styles.linkButton} onClick={() => onRemove(appliedPromo.code)}>
            {t("booking_widget.checkout.remove")}
          </Button>
        </Flex>
      </Flex>
    )
  } else if (isEditing) {
    promoBlock = (
      <Flex vertical gap={8}>
        <label htmlFor="widget-promo-code" className={styles.promoLabel}>
          {t("booking_widget.checkout.promo_label")}
        </label>
        <Input
          id="widget-promo-code"
          size="large"
          value={code}
          status={error ? "error" : ""}
          disabled={isValidating}
          onChange={(event) => {
            setCode(event.target.value.toUpperCase())
            setError(null)
          }}
          onPressEnter={handleApply}
        />
        {error && (
          <span className={styles.errorText} role="alert">
            {error}
          </span>
        )}
        <Flex gap={8}>
          <Button shape="round" color="primary" variant="outlined" onClick={handleCancel}>
            {t("booking_widget.checkout.cancel")}
          </Button>
          <Button shape="round" type="primary" loading={isValidating} disabled={!code.trim()} onClick={handleApply}>
            {t("booking_widget.checkout.apply")}
          </Button>
        </Flex>
      </Flex>
    )
  } else {
    promoBlock = (
      <Button type="link" className={styles.linkButton} onClick={() => setIsEditing(true)}>
        {t("booking_widget.checkout.add_promo")}
      </Button>
    )
  }

  const originalSubtotal = getOriginalIfDiscounted(amounts.originalSubtotal, amounts.subtotal)
  // With a promo code: Subtotal (the price before taxes and fees) - Promo Discount + Taxes & Fees
  // (worked out after the promo) = Order Total, to the cent (QA #24). Taxes & Fees takes the rounding,
  // so the rows always add up to the total charged.
  const shownSubtotal = roundToCents(amounts.baseSubtotal || 0)
  const shownDiscount = roundToCents(amounts.promoDiscount || 0)
  const shownTaxes = Math.max(0, roundToCents(roundToCents(amounts.total) - shownSubtotal + shownDiscount))
  const taxesLine = isTaxesLineShown && amounts.taxesAndFees > 0 && (
    <Flex justify="space-between">
      <span>{t("booking_widget.experience.summary.taxes_and_fees")}</span>
      <span>{numberToCurrency(amounts.taxesAndFees)}</span>
    </Flex>
  )

  return (
    <Flex vertical gap={12} className={styles.summaryCard}>
      {promoBlock}
      <div className={styles.divider} />
      {appliedPromo ? (
        <Flex vertical gap={4}>
          <Flex justify="space-between" className={styles.strongRow}>
            <span>{t("booking_widget.checkout.subtotal")}</span>
            <span>{numberToCurrency(shownSubtotal)}</span>
          </Flex>
          <Flex justify="space-between">
            <span>{t("booking_widget.checkout.promo_discount")}</span>
            <span>-{numberToCurrency(shownDiscount)}</span>
          </Flex>
          {shownTaxes > 0 && (
            <Flex justify="space-between">
              <span>{t("booking_widget.experience.summary.taxes_and_fees")}</span>
              <span>{numberToCurrency(shownTaxes)}</span>
            </Flex>
          )}
          <Flex justify="space-between" className={styles.strongRow}>
            <span>{t("booking_widget.checkout.order_total")}</span>
            <span>{numberToCurrency(amounts.total)}</span>
          </Flex>
        </Flex>
      ) : (
        <Flex vertical gap={4}>
          {taxesLine}
          <Flex justify="space-between" className={styles.strongRow}>
            <span>{t("booking_widget.checkout.order_total")}</span>
            <span>
              {originalSubtotal != null && <span className={styles.struck}>{numberToCurrency(originalSubtotal)}</span>}
              {numberToCurrency(amounts.total)}
            </span>
          </Flex>
        </Flex>
      )}
    </Flex>
  )
}

PromoSection.propTypes = {
  amounts: PropTypes.object.isRequired,
  appliedPromo: PropTypes.shape({ code: PropTypes.string, label: PropTypes.string }),
  isTaxesLineShown: PropTypes.bool,
  onValidate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired
}

PromoSection.defaultProps = {
  appliedPromo: null,
  isTaxesLineShown: false
}

// A booking's price lines (guests or tickets, add-ons) and Taxes & Fees.
const PriceLines = ({ breakdown }) => {
  const { t } = useTranslation()
  const { lines, taxesAndFees } = groupBreakdownLines(breakdown)
  return (
    <>
      {lines.map((line) => (
        <Flex key={line.key} justify="space-between" gap={12}>
          <span>{t(`booking_widget.experience.summary.${line.kind}`, { count: line.count, title: line.title })}</span>
          <span>{numberToCurrency(line.value)}</span>
        </Flex>
      ))}
      {taxesAndFees > 0 && (
        <Flex justify="space-between" gap={12}>
          <span>{t("booking_widget.experience.summary.taxes_and_fees")}</span>
          <span>{numberToCurrency(taxesAndFees)}</span>
        </Flex>
      )}
    </>
  )
}

PriceLines.propTypes = {
  breakdown: PropTypes.array
}

PriceLines.defaultProps = {
  breakdown: []
}

// A booking within 24 hours in an order: a request the guide accepts or declines, and when it is paid.
const ApprovalNote = ({ isChargedOnApproval }) => {
  const { t } = useTranslation()
  return (
    <Flex vertical gap={2} className={styles.approvalNote}>
      <span className={styles.summaryLabel}>{t("booking_widget.checkout.approval_label")}</span>
      <span>{t(isChargedOnApproval ? "booking_widget.checkout.approval_card" : "booking_widget.checkout.approval_paypal")}</span>
    </Flex>
  )
}

ApprovalNote.propTypes = {
  isChargedOnApproval: PropTypes.bool.isRequired
}

// One booking of the order: date and time, guests, price lines and its total before the promo code.
const BookingSummaryCard = ({ experience, selection, amounts, isRequest, isChargedOnApproval }) => {
  const { t } = useTranslation()
  const coverImage = getExperienceCoverImage(experience)
  const originalSubtotal = getOriginalIfDiscounted(amounts.originalSubtotal, amounts.subtotal)

  return (
    <Flex vertical gap={8} className={styles.summaryCard}>
      <Flex align="center" gap={12}>
        <div className={styles.summaryImage}>{coverImage && <img src={coverImage} alt={experience?.title} />}</div>
        <span className={styles.summaryTitle}>{experience?.title}</span>
      </Flex>
      {isRequest && <ApprovalNote isChargedOnApproval={isChargedOnApproval} />}
      <Flex vertical gap={4}>
        <span className={styles.summaryLabel}>{t("booking_widget.checkout.summary_date")}</span>
        {describeBookingDateLines({ experience, selection, t }).map((line) => (
          <span key={line}>{line}</span>
        ))}
      </Flex>
      <div className={styles.divider} />
      <Flex vertical gap={4}>
        <span className={styles.summaryLabel}>{t("booking_widget.checkout.summary_guests")}</span>
        <span>
          {isTicketedSelection(selection)
            ? describeTickets({ experience, ticketCounts: selection.ticketCounts, t })
            : describeGuests({ counts: selection?.counts, t })}
        </span>
      </Flex>
      <div className={styles.divider} />
      <Flex vertical gap={2}>
        <span className={styles.summaryLabel}>{t("booking_widget.checkout.summary_price")}</span>
        <PriceLines breakdown={amounts.breakdown} />
      </Flex>
      <div className={styles.divider} />
      <Flex justify="space-between" className={styles.strongRow}>
        <span>{t("booking_widget.checkout.total")}</span>
        <span>
          {originalSubtotal != null && <span className={styles.struck}>{numberToCurrency(originalSubtotal)}</span>}
          {numberToCurrency(amounts.subtotal)}
        </span>
      </Flex>
      {originalSubtotal != null && (
        <span className={styles.savedText}>
          {t("booking_widget.checkout.saved", { amount: numberToCurrency(originalSubtotal - amounts.subtotal) })}
        </span>
      )}
    </Flex>
  )
}

BookingSummaryCard.propTypes = {
  experience: PropTypes.object.isRequired,
  selection: PropTypes.object.isRequired,
  amounts: PropTypes.object.isRequired,
  isRequest: PropTypes.bool,
  isChargedOnApproval: PropTypes.bool
}

BookingSummaryCard.defaultProps = {
  isRequest: false,
  isChargedOnApproval: true
}

// A booking below 992px (Figma 6058:146133): photo and title (two lines at most), date and guests,
// "Price Details" folded until opened, and its total with what the guest saved.
const CompactBookingCard = ({ experience, selection, amounts, isRequest, isChargedOnApproval }) => {
  const { t } = useTranslation()
  const [isPriceOpen, setIsPriceOpen] = useState(false)
  const coverImage = getExperienceCoverImage(experience)
  const originalSubtotal = getOriginalIfDiscounted(amounts.originalSubtotal, amounts.subtotal)

  return (
    <Flex vertical gap={8} className={styles.compactCard}>
      <Flex align="center" gap={12}>
        <div className={styles.compactImage}>{coverImage && <img src={coverImage} alt={experience?.title} />}</div>
        <span className={styles.compactTitle}>{experience?.title}</span>
      </Flex>
      {isRequest && <ApprovalNote isChargedOnApproval={isChargedOnApproval} />}
      <Flex vertical className={styles.compactMeta}>
        {describeBookingDateLines({ experience, selection, t }).map((line) => (
          <span key={line}>{line}</span>
        ))}
        <span>
          {isTicketedSelection(selection)
            ? describeTickets({ experience, ticketCounts: selection.ticketCounts, t })
            : describeGuests({ counts: selection?.counts, t })}
        </span>
      </Flex>
      <div className={styles.divider} />
      <Flex vertical gap={4} className={styles.compactMeta}>
        <button
          type="button"
          className={styles.compactToggle}
          aria-expanded={isPriceOpen}
          aria-label={t(isPriceOpen ? "booking_widget.checkout.hide_price_details" : "booking_widget.checkout.show_price_details")}
          onClick={() => setIsPriceOpen(!isPriceOpen)}
        >
          <span className={styles.compactLabel}>{t("booking_widget.checkout.summary_price")}</span>
          <Icon type="feather" name={isPriceOpen ? "ChevronUp" : "ChevronDown"} width={16} height={16} color="currentColor" />
        </button>
        {isPriceOpen && <PriceLines breakdown={amounts.breakdown} />}
      </Flex>
      <div className={styles.divider} />
      <Flex vertical align="flex-end">
        <span className={styles.compactTotal}>
          {originalSubtotal != null && <span className={styles.compactStruck}>{numberToCurrency(originalSubtotal)}</span>}
          {numberToCurrency(amounts.subtotal)}
        </span>
        {originalSubtotal != null && (
          <span className={styles.savedText}>{t("booking_widget.checkout.saved", { amount: numberToCurrency(originalSubtotal - amounts.subtotal) })}</span>
        )}
      </Flex>
    </Flex>
  )
}

CompactBookingCard.propTypes = {
  experience: PropTypes.object.isRequired,
  selection: PropTypes.object.isRequired,
  amounts: PropTypes.object.isRequired,
  isRequest: PropTypes.bool,
  isChargedOnApproval: PropTypes.bool
}

CompactBookingCard.defaultProps = {
  isRequest: false,
  isChargedOnApproval: true
}

// The order's summary: a card per booking, then the order's promo code and total. Beside the form
// from 992px; below that it sits above the form as "Order Summary · N experiences", folded to the
// promo code and total until opened. A booking that is a request (isRequest) says so, and when it is
// paid (isChargedOnApproval: by card on the guide's acceptance, else now).
const CheckoutSummary = ({ bookings, isChargedOnApproval, orderAmounts, appliedPromo, onValidatePromo, onRemovePromo }) => {
  const { t } = useTranslation()
  const isCompact = useMediaQuery({ maxWidth: 991 })
  const [isOpen, setIsOpen] = useState(false)
  const promo = (
    <PromoSection
      amounts={orderAmounts}
      appliedPromo={appliedPromo}
      isTaxesLineShown={bookings.length > 1}
      onValidate={onValidatePromo}
      onRemove={onRemovePromo}
    />
  )

  if (isCompact) {
    return (
      <Flex vertical gap={4} className={styles.compactSummary}>
        <button
          type="button"
          className={styles.orderSummaryToggle}
          aria-expanded={isOpen}
          aria-label={t(isOpen ? "booking_widget.checkout.hide_order_summary" : "booking_widget.checkout.show_order_summary")}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className={styles.orderSummaryTitle}>{t("booking_widget.checkout.order_summary")}</span>
          <span className={styles.orderSummaryCount}>{t("booking_widget.checkout.order_summary_count", { count: bookings.length })}</span>
          <Icon type="feather" name={isOpen ? "ChevronUp" : "ChevronDown"} width={16} height={16} color="currentColor" />
        </button>
        {isOpen &&
          bookings.map(({ key, experience, selection, amounts, isRequest }) => (
            <CompactBookingCard
              key={key}
              experience={experience}
              selection={selection}
              amounts={amounts}
              isRequest={isRequest}
              isChargedOnApproval={isChargedOnApproval}
            />
          ))}
        {promo}
      </Flex>
    )
  }

  return (
    <Flex vertical gap={8}>
      {bookings.map(({ key, experience, selection, amounts, isRequest }) => (
        <BookingSummaryCard
          key={key}
          experience={experience}
          selection={selection}
          amounts={amounts}
          isRequest={isRequest}
          isChargedOnApproval={isChargedOnApproval}
        />
      ))}
      {promo}
    </Flex>
  )
}

CheckoutSummary.propTypes = {
  bookings: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      experience: PropTypes.object.isRequired,
      selection: PropTypes.object.isRequired,
      amounts: PropTypes.object.isRequired,
      isRequest: PropTypes.bool
    })
  ).isRequired,
  isChargedOnApproval: PropTypes.bool,
  orderAmounts: PropTypes.object.isRequired,
  appliedPromo: PropTypes.object,
  onValidatePromo: PropTypes.func.isRequired,
  onRemovePromo: PropTypes.func.isRequired
}

CheckoutSummary.defaultProps = {
  isChargedOnApproval: true,
  appliedPromo: null
}

export default CheckoutSummary
